import React, { useState, useEffect, useMemo } from 'react';
import teamsData from '../data/teams_data.json';
import teamTiersData from '../data/team_tiers.json';
import { useDialog } from './DialogProvider';
import './CreateTeamModal.css';

interface CreateTeamPanelProps {
  onClose: () => void;
  onSubmit: (teamName: string, region: string, rulesetMode: 'league' | 'exhibition', goldBudget: number, tier: number) => void;
}

export interface TierBadge {
  label: string;
  background: string;
  color: string;
}

export function getTierBadge(tier: number): TierBadge {
  switch (tier) {
    case 1:
      return { 
        label: 'Tier 1', 
        background: 'linear-gradient(135deg, #f8fafc 0%, #cbd5e1 100%)', 
        color: '#0f172a' 
      };
    case 2:
      return { 
        label: 'Tier 2', 
        background: 'linear-gradient(135deg, #fef08a 0%, #eab308 100%)', 
        color: '#0f172a' 
      };
    case 3:
      return { 
        label: 'Tier 3', 
        background: 'linear-gradient(135deg, #cbd5e1 0%, #64748b 100%)', 
        color: '#0f172a' 
      };
    case 4:
    default:
      return { 
        label: 'Tier 4', 
        background: 'linear-gradient(135deg, #ffedd5 0%, #b45309 100%)', 
        color: '#0f172a' 
      };
  }
}

export const CreateTeamPanel: React.FC<CreateTeamPanelProps> = ({ onClose, onSubmit }) => {
  const [teamName, setTeamName] = useState('');
  const [region, setRegion] = useState('');
  const [rulesetMode, setRulesetMode] = useState<'league' | 'exhibition'>('league');
  const [goldBudget, setGoldBudget] = useState(1000000);
  const { alert } = useDialog();

  // Sorting state for the inline team table
  const [sortKey, setSortKey] = useState<'team' | 'tier'>('team');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleRegionChange = (newRegion: string) => {
    setRegion(newRegion);
    // If name is empty, or matches the old default, update it
    if (!teamName || teamName === `${region} Team` || region === '') {
      setTeamName(`${newRegion} Team`);
    }
  };

  const handleGoldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/,/g, '');
    const num = parseInt(raw, 10);
    setGoldBudget(isNaN(num) ? 0 : num);
  };

  // Auto-update default gold when Ruleset Mode changes, but only if they haven't manually tweaked it away from a default
  useEffect(() => {
    if (rulesetMode === 'league') {
      setGoldBudget(1000000);
    } else {
      setGoldBudget(1150000);
    }
  }, [rulesetMode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamName.trim()) {
      await alert("Please enter a Team Name", "Required Input");
      return;
    }
    if (!region) {
      await alert("Please select a Team", "Required Input");
      return;
    }
    const tier = (teamTiersData as any)[region] || 1;
    onSubmit(teamName.trim(), region, rulesetMode, goldBudget, tier);
  };

  const handleSort = (key: 'team' | 'tier') => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  // Sort teams list dynamically based on selected key and direction
  const sortedTeams = useMemo(() => {
    const list = teamsData.map((t: any) => {
      const tier = (teamTiersData as any)[t.team_name] || 1;
      return {
        team_name: t.team_name,
        tier,
      };
    });

    list.sort((a, b) => {
      if (sortKey === 'team') {
        return sortDirection === 'asc'
          ? a.team_name.localeCompare(b.team_name)
          : b.team_name.localeCompare(a.team_name);
      } else {
        return sortDirection === 'asc'
          ? a.tier - b.tier
          : b.tier - a.tier;
      }
    });

    return list;
  }, [sortKey, sortDirection]);

  return (
    <div 
      className="panel" 
      style={{ 
        maxWidth: '400px', 
        margin: '0 auto 32px auto', 
        padding: '20px', 
        border: '1px solid #334155', 
        borderRadius: '8px', 
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.5)', 
        backgroundColor: '#1e293b', 
        color: '#f8fafc' 
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', flex: 1, textAlign: 'center', paddingLeft: '24px' }}>
          Create New Team
        </h3>
        <button 
          onClick={onClose} 
          style={{ 
            background: 'transparent', 
            border: 'none', 
            color: '#94a3b8', 
            fontSize: '20px', 
            cursor: 'pointer', 
            lineHeight: 1, 
            padding: '4px' 
          }}
          title="Cancel"
        >
          &times;
        </button>
      </div>

      <form onSubmit={handleSubmit} className="create-team-form" style={{ padding: 0 }}>
        {/* Inline Two-Column Sortable Faction Table */}
        <div className="form-group" style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', color: '#cbd5e1', fontSize: '13px' }}>
            Select Team
          </label>
          <div style={{
            border: '1px solid #475569',
            borderRadius: '6px',
            overflow: 'hidden',
            backgroundColor: '#0f172a'
          }}>
            <div style={{ overflowY: 'auto', maxHeight: '280px', scrollBehavior: 'smooth' }}>
              <table className="team-selector-table">
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr>
                    <th 
                      onClick={() => handleSort('team')}
                    >
                      Team {sortKey === 'team' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                    <th 
                      onClick={() => handleSort('tier')}
                      style={{ width: '110px', textAlign: 'center' }}
                    >
                      Tier {sortKey === 'tier' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTeams.map((t) => {
                    const isSelected = region === t.team_name;
                    const badge = getTierBadge(t.tier);
                    return (
                      <tr 
                        key={t.team_name}
                        onClick={() => handleRegionChange(t.team_name)}
                        style={{
                          cursor: 'pointer',
                          borderLeft: isSelected ? '3px solid #ea580c' : '3px solid transparent',
                          transition: 'background-color 0.15s'
                        }}
                        className={`team-row-item ${isSelected ? 'selected-row' : ''}`}
                      >
                        <td style={{ padding: '8px 12px', color: isSelected ? '#ffffff' : '#cbd5e1', fontWeight: isSelected ? 'bold' : 'normal' }}>
                          {t.team_name}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <span style={{
                            background: badge.background,
                            color: badge.color,
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            display: 'inline-block',
                            width: '64px',
                            textAlign: 'center',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                          }}>
                            {badge.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="form-group" style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', color: '#cbd5e1', fontSize: '13px' }}>
            Team Name
          </label>
          <input 
            type="text" 
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            placeholder="Enter team name..."
            required
            style={{
              width: '100%',
              padding: '10px 12px',
              backgroundColor: '#0f172a',
              border: '1px solid #475569',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#f8fafc',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div className="form-row" style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
          <div className="form-group" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', color: '#cbd5e1', fontSize: '13px' }}>
              Ruleset (2025)
            </label>
            <div className="ruleset-toggle-group" style={{ display: 'flex', border: '1px solid #475569', borderRadius: '6px', overflow: 'hidden', backgroundColor: '#0f172a' }}>
              <button
                type="button"
                className={`ruleset-btn ${rulesetMode === 'league' ? 'active' : ''}`}
                onClick={() => setRulesetMode('league')}
                style={{
                  flex: 1,
                  padding: '8px',
                  background: rulesetMode === 'league' ? '#3b82f6' : 'transparent',
                  border: 'none',
                  borderRight: '1px solid #475569',
                  cursor: 'pointer',
                  fontWeight: 600,
                  color: rulesetMode === 'league' ? '#ffffff' : '#94a3b8',
                  fontSize: '12px'
                }}
              >
                🏆 League
              </button>
              <button
                type="button"
                className={`ruleset-btn ${rulesetMode === 'exhibition' ? 'active' : ''}`}
                onClick={() => setRulesetMode('exhibition')}
                style={{
                  flex: 1,
                  padding: '8px',
                  background: rulesetMode === 'exhibition' ? '#3b82f6' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  color: rulesetMode === 'exhibition' ? '#ffffff' : '#94a3b8',
                  fontSize: '12px'
                }}
              >
                🏟️ Exhib
              </button>
            </div>
          </div>

          <div className="form-group" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', color: '#cbd5e1', fontSize: '13px' }}>
              Initial Gold (GP)
            </label>
            <input 
              type="text" 
              value={goldBudget.toLocaleString()}
              onChange={handleGoldChange}
              required
              className="gold-input"
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#0f172a',
                border: '1px solid #475569',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#f8fafc',
                boxSizing: 'border-box',
                textAlign: 'center',
                fontWeight: 600
              }}
            />
          </div>
        </div>

        <div className="modal-actions" style={{ marginTop: '20px' }}>
          <button 
            type="submit" 
            className="create-submit-btn"
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#ea580c',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
          >
            Generate Team
          </button>
        </div>
      </form>
    </div>
  );
};
